import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  IoPencil,
  IoTrashBin,
  IoThumbsUp,
  IoThumbsDown,
  IoChevronDown,
  IoChevronUp,
} from "react-icons/io5";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ClarificationBoard = ({ user, requestId }) => {
  /* ─── STATE ─────────────────────────────────────────── */
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");

  // comment editing
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");

  // reply editing
  const [editingReplyId, setEditingReplyId] = useState(null);
  const [editingReplyText, setEditingReplyText] = useState("");

  // delete comment / reply
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteReplyId, setDeleteReplyId] = useState(null);

  // expand / collapse comment threads
  const [expandedComments, setExpandedComments] = useState({});

  // per-comment reply draft
  const [replyDrafts, setReplyDrafts] = useState({}); // { [commentId]: string }

  /* ─── CONFIG ────────────────────────────────────────── */
  const API_V2 = process.env.REACT_APP_SERVER_URL_V2;
  const API_V1 = API_V2.replace("/api/v2", "/api/v1");
  const accessToken = localStorage.getItem("accessToken");

  /* helper: build profile-photo URL */
  const buildProfileUrl = (userId, profileId) =>
    userId && profileId
      ? `${API_V1}/photos/${userId}/images/${profileId}`
      : null;

  /* sort newest → oldest */
  const sortNewestFirst = (a, b) =>
    new Date(b.created_at || b.createdAt) -
    new Date(a.created_at || a.createdAt);

  /* ─── FETCH COMMENTS + REPLIES ──────────────────────── */
  const fetchComments = useCallback(async () => {
    if (!requestId) return;
    try {
      /* 1️⃣ parent comments */
      const { data } = await axios.get(
        `${API_V2}/quotes/getallcomment/${requestId}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      const top = (Array.isArray(data.data) ? data.data : [data.data || []])
        .filter(Boolean)
        .sort(sortNewestFirst);

      /* 2️⃣ replies */
      const withReplies = await Promise.all(
        top.map(async (comment) => {
          try {
            const res = await axios.get(
              `${API_V1}/quotes/allcommentreply/${comment.id}`,
              { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            const replies = (
              Array.isArray(res.data.data)
                ? res.data.data
                : [res.data.data || []]
            )
              .filter(Boolean)
              .sort(sortNewestFirst);
            return { ...comment, replies };
          } catch {
            /* fallback to V2 or empty */
            try {
              const resV2 = await axios.get(
                `${API_V2}/quotes/getreplycomment/${comment.id}`,
                { headers: { Authorization: `Bearer ${accessToken}` } }
              );
              const repliesV2 = (
                Array.isArray(resV2.data.data)
                  ? resV2.data.data
                  : [resV2.data.data || []]
              )
                .filter(Boolean)
                .sort(sortNewestFirst);
              return { ...comment, replies: repliesV2 };
            } catch {
              return { ...comment, replies: [] };
            }
          }
        })
      );

      setComments(withReplies);
    } catch {
      // toast.error("Failed to load comments.");
    }
  }, [API_V1, API_V2, accessToken, requestId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  /* ─── ADD COMMENT ───────────────────────────────────── */
  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      await axios.post(
        `${API_V2}/quotes/comment/${requestId}`,
        { comment: newComment.trim() },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setNewComment("");
      toast.success("Comment added.");
      fetchComments();
    } catch {
      toast.error("Failed to add comment.");
    }
  };

  /* ─── EDIT COMMENT ──────────────────────────────────── */
  const openEdit = (id, text) => {
    setEditingId(id);
    setEditingText(text);
  };
  const saveEdit = async () => {
    if (!editingText.trim()) return;
    try {
      await axios.put(
        `${API_V2}/quotes/editcomment/${editingId}`,
        { comment: editingText.trim() },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setEditingId(null);
      setEditingText("");
      toast.success("Comment updated.");
      fetchComments();
    } catch {
      toast.error("Failed to update comment.");
    }
  };

  /* ─── EDIT REPLY ────────────────────────────────────── */
  const openEditReply = (id, text) => {
    setEditingReplyId(id);
    setEditingReplyText(text);
  };
  const saveEditReply = async () => {
    if (!editingReplyText.trim()) return;
    try {
      await axios.put(
        `${API_V2}/quotes/editreply/${editingReplyId}`,
        { reply: editingReplyText.trim() },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setEditingReplyId(null);
      setEditingReplyText("");
      toast.success("Reply updated.");
      fetchComments();
    } catch {
      toast.error("Failed to update reply.");
    }
  };

  /* ─── DELETE COMMENT ────────────────────────────────── */
  const confirmDelete = async () => {
    try {
      await axios.delete(`${API_V2}/quotes/deletecomment/${deleteTarget}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setDeleteTarget(null);
      toast.success("Comment deleted.");
      fetchComments();
    } catch {
      toast.error("Failed to delete comment.");
    }
  };

  /* ─── DELETE REPLY ──────────────────────────────────── */
  const confirmDeleteReply = async () => {
    try {
      await axios.delete(`${API_V2}/quotes/delete/${deleteReplyId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setDeleteReplyId(null);
      toast.success("Reply deleted.");
      fetchComments();
    } catch {
      toast.error("Failed to delete reply.");
    }
  };

  /* ─── LIKE / DISLIKE ───────────────────────────────── */
  const handleReaction = async (commentId, type) => {
    try {
      await axios.post(
        `${API_V2}/quotes/likedislike/${commentId}?reaction_type=${type}`,
        null,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      toast.success("Reaction updated.");

      fetchComments(); // ✅ ADDED – refresh counts
    } catch {
      toast.error("Failed to react.");
    }
  };

  /* ─── ADD REPLY ─────────────────────────────────────── */
  const handleAddReply = async (commentId) => {
    const text = (replyDrafts[commentId] || "").trim();
    if (!text) return;
    try {
      await axios.post(
        `${API_V2}/quotes/reply/${commentId}`,
        { reply: text },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setReplyDrafts((p) => ({ ...p, [commentId]: "" }));
      toast.success("Reply added.");
      fetchComments();
    } catch {
      toast.error("Failed to add reply.");
    }
  };

  const toggleReplies = (id) =>
    setExpandedComments((p) => ({
      ...p,
      [id]: !p[id],
    })); /* ─── RENDER ────────────────────────────────────────── */
  return (
    <div className="mt-5 border flex flex-col bg-gray-50 rounded-lg shadow-lg max-h-[600px]">
      {/* Header */}
      <div className="flex-shrink-0 bg-white px-6 py-4 border-b">
        <div className="flex justify-between items-center gap-4">
          <h3 className="text-xl font-semibold text-gray-800">
            Clarification Board
          </h3>
          <h2 className="text-sm text-gray-600">
            No Spam, self-promotion or advertisement is permitted.
          </h2>
        </div>
      </div>

      {/* New comment */}
      <div className="flex-shrink-0 bg-gray-100 p-4 border-t">
        <div className="flex items-center space-x-3">
          <input
            className="flex-1 bg-white border rounded-full px-4 py-2 focus:ring-2 focus:ring-blue-500"
            placeholder="Type a message..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAddComment()}
          />
          <button
            className="bg-blue-600 text-white px-5 py-2 rounded-full hover:bg-blue-700"
            onClick={handleAddComment}
          >
            Send
          </button>
        </div>
      </div>

      {/* Comment list */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-white">
        {comments.length === 0 ? (
          <p className="text-center text-gray-500 italic">No comments yet.</p>
        ) : (
          comments.map((c) => (
            <div key={c.id} className="flex space-x-3">
              {/* avatar */}
              {c.User?.profile_id ? (
                <img
                  src={buildProfileUrl(c.User.id, c.User.profile_id)}
                  alt={`${c.User.first_name} ${c.User.last_name}`}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-blue-400 text-white flex items-center justify-center font-medium">
                  {c.User?.first_name?.[0] || "A"}
                </div>
              )}

              {/* comment bubble */}
              <div className="flex-1">
                <div className="bg-gray-100 rounded-xl px-4 py-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-800">
                      {c.User?.first_name} {c.User?.last_name}
                    </span>

                    {/* comment edit / delete */}
                    <div className="flex items-center space-x-2 text-gray-400">
                      <IoPencil
                        className="w-5 h-5 cursor-pointer hover:text-blue-600"
                        onClick={() => openEdit(c.id, c.comment)}
                      />
                      <IoTrashBin
                        className="w-5 h-5 cursor-pointer hover:text-red-600"
                        onClick={() => setDeleteTarget(c.id)}
                      />
                    </div>
                  </div>

                  {editingId === c.id ? (
                    <div className="mt-2 flex space-x-2">
                      <input
                        className="flex-1 border rounded-md px-2 py-1 focus:ring-2 focus:ring-green-500"
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                      />
                      <button
                        className="text-green-600 font-medium"
                        onClick={saveEdit}
                      >
                        Save
                      </button>
                      <button
                        className="text-gray-600"
                        onClick={() => setEditingId(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <p className="mt-1 text-gray-700">{c.comment}</p>
                  )}

                  {/* like / dislike */}
                  <div className="mt-2 flex items-center justify-between text-sm text-gray-500">
                    <div className="flex space-x-4">
                      <button
                        className="flex items-center space-x-1 "
                        onClick={() => handleReaction(c.id, "like")}
                      >
                        <IoThumbsUp className="w-4 h-4" />
                        <span className="ml-1">{c.like || 0}</span>
                        {/* ★ ADDED count */}
                      </button>
                      <button
                        className="flex items-center space-x-1 "
                        onClick={() => handleReaction(c.id, "dislike")}
                      >
                        <IoThumbsDown className="w-4 h-4" />
                        <span className="ml-1">{c.dislike || 0}</span>
                        {/* ★ ADDED count */}
                      </button>
                    </div>

                    {/* {c.replies.length > 0 && (
                      <button
                        className="flex items-center space-x-1 hover:text-blue-600"
                        onClick={() => toggleReplies(c.id)}
                      >
                        {expandedComments[c.id] ? (
                          <>
                            <IoChevronUp className="w-4 h-4" />
                            <span>Hide {c.replies.length}</span>
                          </>
                        ) : (
                          <>
                            <IoChevronDown className="w-4 h-4" />
                            <span>Replies {c.replies.length}</span>
                          </>
                        )}
                      </button>
                    )} */}
                  </div>
                </div>

                {/* replies */}
                {/* {expandedComments[c.id] && ( */}
                <div className="mt-2 pl-12 space-y-3">
                  {c.replies.map((r) => {
                    const ru = r.User || r.Customer || r.user || null;
                    const isSupplier = r.reply_by_role === "SUPPLIER";

                    return (
                      <div key={r.id} className="flex space-x-3">
                        {/* reply avatar */}
                        {ru?.profile_id ? (
                          <img
                            src={buildProfileUrl(ru.id, ru.profile_id)}
                            alt={`${ru.first_name} ${ru.last_name}`}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gray-300 text-gray-700 flex items-center justify-center font-medium">
                            {ru?.first_name?.[0] || r.author?.[0] || "C"}
                          </div>
                        )}

                        {/* reply bubble */}
                        <div className="flex-1 bg-gray-100 rounded-xl px-3 py-2">
                          <div className="flex items-start justify-between">
                            <span className="font-medium text-gray-800 text-sm">
                              {ru
                                ? `${ru.first_name} ${ru.last_name}`
                                : r.author || "Customer"}
                            </span>

                            {/* edit / delete icons for every SUPPLIER reply */}
                            {isSupplier && (
                              <div className="flex space-x-2 text-gray-400">
                                <IoPencil
                                  className="w-4 h-4 cursor-pointer hover:text-blue-600"
                                  onClick={() => openEditReply(r.id, r.reply)}
                                />
                                <IoTrashBin
                                  className="w-4 h-4 cursor-pointer hover:text-red-600"
                                  onClick={() => setDeleteReplyId(r.id)}
                                />
                              </div>
                            )}
                          </div>

                          {editingReplyId === r.id ? (
                            <div className="mt-1 flex space-x-2">
                              <input
                                className="flex-1 border rounded-md px-2 py-0.5 text-sm focus:ring-2 focus:ring-green-500"
                                value={editingReplyText}
                                onChange={(e) =>
                                  setEditingReplyText(e.target.value)
                                }
                              />
                              <button
                                className="text-green-600 text-sm font-medium"
                                onClick={saveEditReply}
                              >
                                Save
                              </button>
                              <button
                                className="text-sm text-gray-600"
                                onClick={() => setEditingReplyId(null)}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <p className="mt-1 text-gray-700 text-sm">
                              {r.reply || r.comment}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* permanent reply input */}
                  <div className="pl-12 pt-3 flex space-x-2">
                    <input
                      className="flex-1 border rounded-md px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500"
                      placeholder="Type your reply..."
                      value={replyDrafts[c.id] || ""}
                      onChange={(e) =>
                        setReplyDrafts((p) => ({
                          ...p,
                          [c.id]: e.target.value,
                        }))
                      }
                      onKeyDown={(e) =>
                        e.key === "Enter" && handleAddReply(c.id)
                      }
                    />
                    <button
                      className="bg-blue-600 text-white text-sm px-3 py-1 rounded-md hover:bg-blue-700"
                      onClick={() => handleAddReply(c.id)}
                    >
                      Send
                    </button>
                    <button
                      className="text-sm text-gray-600"
                      onClick={() =>
                        setReplyDrafts((p) => ({ ...p, [c.id]: "" }))
                      }
                    >
                      Cancel
                    </button>
                  </div>
                </div>
                {/* )} */}
              </div>
            </div>
          ))
        )}
      </div>

      {/* delete comment confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-80">
            <p className="text-center mb-4">Delete this comment?</p>
            <div className="flex justify-end space-x-4">
              <button
                className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
                onClick={() => setDeleteTarget(null)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* delete reply confirmation */}
      {deleteReplyId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-80">
            <p className="text-center mb-4">Delete this reply?</p>
            <div className="flex justify-end space-x-4">
              <button
                className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
                onClick={() => setDeleteReplyId(null)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                onClick={confirmDeleteReply}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default ClarificationBoard;
